// Google Analytics integration
import siteConfig from '@/config/siteConfig';
import { getCookie } from 'cookies-next';

export const GA_MEASUREMENT_ID = 'G-80Q2TLJCKV';
export const GA_STREAM_ID = '11228569424';

// Initialize Google Analytics
export const initGA = () => {
  if (typeof window !== 'undefined' && !window.gtag) {
    // Consent check
    const consentCookie = getCookie('perxify_consent');
    let analyticsConsent = false;
    try {
      const consents = consentCookie ? JSON.parse(consentCookie as string) : {};
      analyticsConsent = consents.analytics ?? false;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {}
    
    if (!analyticsConsent) return;

    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    
    const domain = new URL(siteConfig.baseUrl).hostname;

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${GA_MEASUREMENT_ID}', {
        send_page_view: true,
        cookie_flags: 'SameSite=None;Secure',
        cookie_domain: '${domain}',
        transport_type: 'beacon',
      });
    `;
    
    document.head.appendChild(script1);
    document.head.appendChild(script2);
  }
};

// Track page views
export const pageview = (url: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
    });
  }
};

// Track events
interface EventParams {
  action: string;
  category: string;
  label?: string;
  value?: number;
};

export const event = ({ action, category, label, value }: EventParams) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
};

// Track conversion events
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const trackConversion = (conversionId: string, label: string = '') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': `${GA_MEASUREMENT_ID}/${conversionId}`,
      'event_callback': () => {
        console.log('Conversion tracked:', conversionId);
      }
    });
  }
};

// Enhanced ecommerce tracking
export const trackProductView = (product: {
  id: string;
  name: string;
  category?: string;
  price?: number;
}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'view_item', {
      currency: 'EUR',
      value: product.price,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_category: product.category,
          price: product.price,
        }
      ]
    });
  }
};

// Track form submissions
export const trackFormSubmission = (formName: string, success: boolean = true) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', success ? 'form_submit_success' : 'form_submit_failure', {
      event_category: 'Forms',
      event_label: formName,
    });
  }
};

// Track demo requests
export const trackDemoRequest = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'generate_lead', {
      event_category: 'Conversion',
      event_label: 'Demo Request',
    });
  }
};

// Track UTM parameters
export const trackUTMParameters = () => {
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const utmCampaign = urlParams.get('utm_campaign');
    const utmContent = urlParams.get('utm_content');
    const utmTerm = urlParams.get('utm_term');
    
    if (utmSource || utmMedium || utmCampaign || utmContent || utmTerm) {
      const utmData = {
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_content: utmContent,
        utm_term: utmTerm,
      };
      
      // Store UTM parameters in session storage for cross-page tracking
      sessionStorage.setItem('utm_data', JSON.stringify(utmData));
      
      // Send UTM data to Google Analytics
      if (window.gtag) {
        window.gtag('event', 'campaign_attributed', {
          event_category: 'Marketing',
          event_label: utmSource,
          ...utmData
        });
      }
    }
  }
};

// Get stored UTM parameters
export const getUTMParameters = () => {
  if (typeof window !== 'undefined') {
    const utmData = sessionStorage.getItem('utm_data');
    return utmData ? JSON.parse(utmData) : null;
  }
  return null;
};

// Track user engagement
export const trackEngagement = (action: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: 'Engagement',
      value: value,
    });
  }
};

// Initialize cross-domain tracking
export const initCrossDomainTracking = (domains: string[]) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', GA_MEASUREMENT_ID, {
      linker: {
        domains: domains,
        accept_incoming: true
      }
    });
  }
};

// Track CTA button clicks
export const trackCTAClick = (ctaName: string, location: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'cta_click', {
      event_category: 'CTA',
      event_label: ctaName,
      custom_parameter_location: location,
    });
  }
};

// Track navigation clicks
export const trackNavigation = (linkName: string, destination: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'navigation_click', {
      event_category: 'Navigation',
      event_label: linkName,
      custom_parameter_destination: destination,
    });
  }
};

// Track social media clicks (external links)
export const trackSocialClick = (platform: string, action: string = 'click') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'social_click', {
      event_category: 'Social Media',
      event_label: platform,
      custom_parameter_action: action,
    });
  }
};

// Track feature exploration
export const trackFeatureExploration = (featureName: string, source: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'feature_exploration', {
      event_category: 'Features',
      event_label: featureName,
      custom_parameter_source: source,
    });
  }
};
